import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import Cookies from 'universal-cookie'
import { useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { useWhitelabelDetection } from './WhitelabelDetectionContext'
import { AI_STUDIO_PLANS, SOCIAL_PLANS } from 'views/subscription'
import {
    useCurrentUser,
    useLogout as useLogoutMutation,
    useUpdateCurrentUser,
    useCreateApiKey as useCreateApiKeyMutation,
    useRevokeToken as useRevokeTokenMutation,
} from 'api/hooks/useSession'
import { useCreateCompany } from 'api/hooks/useCompanies'
import { useSubscriptionBalance } from 'api/hooks/useSession'
import { api } from 'api/http'
import { queryKeys } from 'api/queryKeys'

/**
 * Thin compatibility shim around the React Query hooks. Preserves the
 * `{ session, user, actions, updateUserData, updateBalance, getBalance,
 *    editUserRole, isLoggedIn, planType }` shape consumed by ~120 files so
 * those call sites can be migrated incrementally instead of all at once.
 *
 * All data fetching is delegated to `useCurrentUser` (RQ — cached, deduped,
 * shared across the tree). All side effects (Intercom, Sleekplan, referrer
 * tracking, language/timezone backfill, balance polling) are preserved.
 */

const SessionContext = React.createContext({
    session: undefined,
    user: undefined,
    actions: {},
    updateUserData: () => {},
    updateBalance: () => {},
    getBalance: () => {},
    editUserRole: () => {},
    isLoggedIn: false,
    planType: null,
})

const _getActualCompanyID = (user) => {
    if (!user?.id) return undefined
    try {
        const stored = localStorage.getItem(`actualCompanyID${user.id}`)
        // Only trust a numeric stored id; otherwise fall back to the profile so
        // a corrupt value ("[object Object]") never reaches the API.
        if (stored && /^\d+$/.test(stored)) return stored
    } catch (_) {}
    return user?.companies?.[0]?.id
}

const SessionProvider = ({ children }) => {
    const { whitelabelUserId, mustBeWhitelabeled } = useWhitelabelDetection()
    const locale = useSelector((state) => state.locale.currentLang)
    const qc = useQueryClient()

    const { data: rawUser, refetch: refetchUser } = useCurrentUser()
    const { mutateAsync: logoutMutate } = useLogoutMutation()
    const { mutateAsync: updateMeMutate } = useUpdateCurrentUser()
    const { mutateAsync: createApiKeyMutate } = useCreateApiKeyMutation()
    const { mutateAsync: revokeTokenMutate } = useRevokeTokenMutation()
    const { mutateAsync: createCompanyMutate } = useCreateCompany()

    const activeCompanyId = rawUser ? _getActualCompanyID(rawUser) : undefined
    const { data: balance } = useSubscriptionBalance(activeCompanyId, {
        enabled: !!activeCompanyId && !!rawUser?.id,
    })

    // The combined user object exposed to consumers: profile + subscription +
    // balance, matching the shape SessionContext.user always had.
    // Tolerates either rawUser.subscriptions (array, from /me) or
    // rawUser.subscription (singular, when seeded from a legacy localStorage
    // snapshot written by the pre-RQ code).
    const user = useMemo(() => {
        if (!rawUser?.id) return undefined
        const fromArray = rawUser?.subscriptions?.find(
            (s) =>
                s?.stripe_status === 'active' || s?.stripe_status === 'trialing'
        )
        const activeSubscription = fromArray ?? rawUser?.subscription
        return {
            ...rawUser,
            subscription:
                activeSubscription &&
                !(
                    activeSubscription?.stripe_status === 'canceled' &&
                    activeSubscription?.ends_at &&
                    new Date(activeSubscription.ends_at) < new Date()
                )
                    ? activeSubscription
                    : undefined,
            balance,
        }
    }, [rawUser, balance])

    const isLoggedIn = !!user?.id

    // Persist the raw /me response so useCurrentUser can seed itself from
    // localStorage on the next refresh (avoids login-screen flash). We store
    // rawUser specifically — not the derived `user` — so the cached snapshot
    // keeps `subscriptions` as an array and the derivation memo above can
    // re-resolve the active subscription.
    useEffect(() => {
        if (!rawUser?.id) return
        try {
            localStorage.setItem('user', JSON.stringify(rawUser))
        } catch (_) {}
    }, [rawUser])

    // ─── One-time per session side effects ──────────────────────────────────
    const firstCompanyCreatedRef = useRef(false)
    const intercomBootedRef = useRef(false)
    const sleekplanBootedRef = useRef(false)
    const referralProcessedRef = useRef(false)
    const referrerBackfilledRef = useRef(false)
    const languageBackfilledRef = useRef(false)
    const timezoneBackfilledRef = useRef(false)

    // Create first company for brand-new users
    useEffect(() => {
        if (!rawUser?.id) return
        if (firstCompanyCreatedRef.current) return
        if (mustBeWhitelabeled) return
        if (rawUser?.companies?.length) return
        firstCompanyCreatedRef.current = true
        ;(async () => {
            try {
                const newCompany = await createCompanyMutate({
                    type: 'company',
                })
                if (newCompany?.id) {
                    try {
                        localStorage.setItem(
                            'recentlyCreatedCompany',
                            String(newCompany.id)
                        )
                    } catch (_) {}
                }
                await refetchUser()
            } catch (e) {
                console.error(e)
            }
        })()
    }, [
        rawUser?.id,
        rawUser?.companies?.length,
        mustBeWhitelabeled,
        createCompanyMutate,
        refetchUser,
    ])

    // Referral cookie consumption
    useEffect(() => {
        if (!rawUser?.id) return
        if (referralProcessedRef.current) return
        const cookies = new Cookies()
        const referral = cookies.get('referral')
        if (!referral) return
        if (
            new Date(rawUser.created_at).getTime() + 24 * 3600 * 1000 >
            Date.now()
        ) {
            referralProcessedRef.current = true
            api.put(`/users/${rawUser.id}`, {}, { referral_code: referral })
                .then(() => cookies.remove('referral'))
                .catch(() => {})
        }
    }, [rawUser?.id, rawUser?.created_at])

    // Referrer / land URL backfill
    useEffect(() => {
        if (!rawUser?.id) return
        if (referrerBackfilledRef.current) return
        try {
            const cookies = new Cookies()
            if (
                !rawUser?.referrer_url &&
                !rawUser?.land_url &&
                cookies.get('referrer_url') &&
                cookies.get('referrer_url') !== 'null' &&
                cookies.get('land_url') &&
                cookies.get('land_url') !== 'null' &&
                new Date(rawUser?.created_at) > new Date('2024-05-29 13:00:00')
            ) {
                referrerBackfilledRef.current = true
                api.put(
                    `/users/${rawUser.id}`,
                    {},
                    {
                        referrer_url: cookies.get('referrer_url') || 'null',
                        land_url: cookies.get('land_url') || 'null',
                    }
                ).catch(() => {})
            }
        } catch (_) {}
    }, [rawUser?.id])

    // Language backfill
    useEffect(() => {
        if (!rawUser?.id) return
        if (languageBackfilledRef.current) return
        if (rawUser?.language) return
        if (!locale) return
        languageBackfilledRef.current = true
        api.put(`/users/${rawUser.id}`, {}, { language: locale }).catch(() => {})
    }, [rawUser?.id, rawUser?.language, locale])

    // Timezone backfill
    useEffect(() => {
        if (!rawUser?.id) return
        if (timezoneBackfilledRef.current) return
        if (rawUser?.timezone) return
        try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
            if (!tz) return
            timezoneBackfilledRef.current = true
            api.put(`/users/${rawUser.id}`, {}, { timezone: tz }).catch(() => {})
        } catch (_) {}
    }, [rawUser?.id, rawUser?.timezone])

    // Intercom boot
    useEffect(() => {
        if (!user?.id) return
        if (intercomBootedRef.current) return
        intercomBootedRef.current = true
        try {
            window.Intercom('boot', {
                app_id: 'p63otiu3',
                name: user?.name,
                email: user?.email,
                user_id: user?.id,
                created_at: Math.ceil(
                    new Date(user?.created_at).getTime() / 1000
                ),
                company_count: user?.companies?.length,
                own_company_count: user?.companies?.filter(
                    (item) => (item?.user?.id || item?.user_id) == user?.id
                )?.length,
                invited_company_count: user?.companies?.filter(
                    (item) => (item?.user?.id || item?.user_id) != user?.id
                )?.length,
                subscription_plan:
                    user?.subscription?.items?.[0]?.product?.name,
                subscription_status_list: user?.subscription
                    ? user?.subscription?.stripe_status
                    : 'no_subscription',
                language: user?.language ? user?.language : 'en',
                credit_card_last_four: user?.pm_last_four,
                credit_card_type: user?.pm_type,
            })
        } catch (_) {}
    }, [user?.id])

    // Sleekplan boot
    useEffect(() => {
        if (!user?.id) return
        if (sleekplanBootedRef.current) return
        sleekplanBootedRef.current = true
        try {
            let weight = 1
            if (user?.subscription?.stripe_status === 'active') weight = 2
            if (user?.subscription?.items?.[0]?.product?.name === 'team')
                weight = 3
            if (user?.subscription?.items?.[0]?.product?.name === 'agency')
                weight = 4
            window.$sleek?.setUser?.({
                mail: user?.email,
                id: user?.id,
                name: user?.name,
                img: user?.image?.url,
                weight,
            })
        } catch (_) {}
    }, [user?.id])

    // Push balance changes into Intercom (mirrors legacy updateBalance side
    // effect — runs whenever the balance object changes shape).
    useEffect(() => {
        if (!user?.id || !balance) return
        try {
            window.Intercom?.('update', {
                balance_words_spent: balance?.words_spent,
                balance_words_allowed: balance?.words_allowed,
                balance_companies_spent: balance?.companies_spent,
                balance_companies_allowed: balance?.companies_allowed,
                balance_users_spent: balance?.users_spent,
                balance_users_allowed: balance?.users_allowed,
                balance_postGroups_spent: balance?.postGroups_spent,
                balance_postGroups_allowed: balance?.postGroups_allowed,
                balance_words_renews_at: balance?.words_renews_at
                    ? Math.ceil(
                          new Date(balance?.words_renews_at).getTime() / 1000
                      )
                    : undefined,
                balance_api_keys_enabled: balance?.api_keys_enabled,
                balance_white_label_enabled: balance?.white_label_enabled,
            })
        } catch (_) {}
    }, [user?.id, balance])

    // ─── Stable callbacks consumed by old code ──────────────────────────────
    // All callbacks are memoized so consumers that put them in effect deps
    // (e.g. CompanyContext's `useEffect([activeCompanyId, updateBalance])`)
    // don't re-fire on every SessionProvider render — that previously caused
    // an infinite invalidate→refetch→rerender loop on /subscriptions/balance.
    const updateUserData = useCallback(async () => {
        const result = await refetchUser()
        return result?.data
    }, [refetchUser])

    const userIdForCallbacks = user?.id
    const updateBalance = useCallback(() => {
        if (!userIdForCallbacks) return
        const cid =
            _getActualCompanyID(rawUser) || rawUser?.companies?.[0]?.id
        if (!cid) return
        qc.invalidateQueries({
            queryKey: queryKeys.session.balance(cid),
        })
    }, [qc, userIdForCallbacks, rawUser])

    const getBalance = useCallback(async (companyId, userId) => {
        const params = {}
        if (companyId) params.company_id = companyId
        if (userId) params.user_id = userId
        const response = await api.get('/subscriptions/balance', params)
        return response?.data
    }, [])

    const editUserRole = useCallback(async (companyId, userId, role) => {
        const response = await api.put(
            `/companies/${companyId}/users/${userId}`,
            {},
            { role }
        )
        qc.invalidateQueries({ queryKey: queryKeys.users.all })
        qc.invalidateQueries({ queryKey: queryKeys.companies.all })
        return response?.data
    }, [qc])

    const actions = useMemo(
        () => ({
            login: async (token) => {
                try {
                    localStorage.setItem('token', token)
                    await refetchUser()
                } catch (e) {
                    try {
                        localStorage.removeItem('token')
                        localStorage.removeItem('user')
                    } catch (_) {}
                    console.error(e)
                }
            },
            logout: async () => {
                try {
                    await logoutMutate()
                } catch (e) {
                    console.error(e)
                }
            },
            revokeToken: async (tokenId) => {
                await revokeTokenMutate({ id: tokenId, revoke: true })
            },
            createApiKey: async (name, companyId) => {
                return await createApiKeyMutate({
                    name,
                    is_api_key: true,
                    company_id: companyId,
                })
            },
            updatePassword: async (password) => {
                if (!user?.id) return
                return await updateMeMutate({
                    id: user.id,
                    password,
                })
            },
        }),
        [
            refetchUser,
            logoutMutate,
            revokeTokenMutate,
            createApiKeyMutate,
            updateMeMutate,
            user?.id,
        ]
    )

    const planType = useMemo(() => {
        const planName = user?.subscription?.items?.find(
            (item) => item.product?.type === 'plan'
        )?.product?.name
        return AI_STUDIO_PLANS.includes(planName) ? 'ai_studio' : 'social'
    }, [user?.subscription?.items])

    const session = useMemo(
        () => ({
            isLoggedIn,
            user,
        }),
        [isLoggedIn, user]
    )

    const visibleUser = useMemo(() => {
        if (!user?.id) return undefined
        if (mustBeWhitelabeled && whitelabelUserId) {
            return {
                ...user,
                companies:
                    whitelabelUserId !== user.id
                        ? user?.companies?.filter(
                              (c) => c?.user?.id == whitelabelUserId
                          )
                        : user?.companies,
            }
        }
        return user
    }, [user, mustBeWhitelabeled, whitelabelUserId])

    return (
        <SessionContext.Provider
            value={{
                session,
                actions,
                updateUserData,
                updateBalance,
                getBalance,
                editUserRole,
                user: visibleUser,
                isLoggedIn,
                planType,
            }}
        >
            {children}
        </SessionContext.Provider>
    )
}

export { SessionContext, SessionProvider }
